import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Form,
  Spinner,
  Card,
  Badge,
} from "react-bootstrap";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { toast } from "react-toastify";
import { FaHistory } from "react-icons/fa";
import { useTypeConfig } from "../../../utils/useTypeConfig";

const LeadViewModal = ({
  show,
  handleClose,
  leadId,
  lead,
  fetchLeadData,
  setLead,
  fetchAllLeadData,
}) => {
  const [leadData, setLeadData] = useState([]);
  const { labels, hiddenFields } = useTypeConfig();
  const [leadHistoryData, setLeadHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNextActionModal, setShowNextActionModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [interactionData, setInteractionData] = useState({
    date: "",
    time: "",
    notes: "",
  });
  const [nextActionData, setNextActionData] = useState({ date: "", notes: "" });
  const userId = localStorage.getItem("userId");
  const [errors, setErrors] = useState({});
  const leadLogs = Array.isArray(leadHistoryData?.logs)
    ? leadHistoryData.logs
    : [];

  useEffect(() => {
    const fetchDetailData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/api/lead-logs/${leadId}/`,
          { headers: HEADERS },
        );
        setLeadHistoryData(response.data);
      } catch (err) {
        console.error("Error fetching lead data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) fetchDetailData();
    else setLeadHistoryData([]);
  }, [show, leadId]);

  useEffect(() => {
    const fetchLeadInteractions = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/api/interactions/?lead_id=${leadId}`,
          {
            headers: HEADERS,
          },
        );
        if (response) {
          setLeadData(response.data);
        }
      } catch (err) {
        console.error("Error fetching lead data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchLeadData();
      fetchLeadInteractions();
    } else {
      setLeadData([]);
    }
  }, [show, leadId]);

  const validateNextActionForm = () => {
    const newErrors = {};
    if (!nextActionData.date)
      newErrors.date = "Next Interaction Date is required.";
    if (!nextActionData.notes) newErrors.notes = "Notes are required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateInteractionForm = () => {
    const newErrors = {};
    if (!interactionData.date) newErrors.date = "Date is required.";
    if (!interactionData.time) newErrors.time = "Time is required.";
    if (!interactionData.notes) newErrors.notes = "Notes are required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInteractionSubmit = async () => {
    if (!validateInteractionForm()) return;

    const formDataToSubmit = {
      previous_interaction_date: interactionData.date,
      previous_interaction_time: interactionData.time,
      notes: interactionData.notes,
      lead: leadId,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/api/interactions/`,
        formDataToSubmit,
        {
          headers: { ...HEADERS },
        },
      );

      if (response && response.data) {
        const { created_by } = response.data;

        setLeadData((prevData) => [
          ...prevData,
          {
            ...response.data,
            created_by: {
              first_name: created_by.first_name,
              last_name: created_by.last_name,
            },
          },
        ]);

        toast.success("Interaction added successfully");
        fetchAllLeadData();

        const leadResponse = await axios.get(
          `${BASE_URL}/api/leads/${leadId}/`,
          { headers: HEADERS },
        );

        if (leadResponse && leadResponse.data) {
          fetchLeadData();
          setLead(leadResponse.data);
        }

        setInteractionData({ date: "", time: "", notes: "" });
        setShowInteractionModal(false);
      }
    } catch (error) {}
  };

  const handleNextActionSubmit = async () => {
    if (!validateNextActionForm()) return;

    const formDataToSubmit = {
      next_interaction: nextActionData.date,
      next_interaction_notes: nextActionData.notes,
      next_interaction_user: userId,
    };

    try {
      const response = await axios.put(
        `${BASE_URL}/api/leads/${leadId}/`,
        formDataToSubmit,
        { headers: { ...HEADERS } },
      );

      if (response && response.data) {
        toast.success("Next Action added successfully.");
        fetchAllLeadData();

        const leadResponse = await axios.get(
          `${BASE_URL}/api/leads/${leadId}/`,
          { headers: HEADERS },
        );

        if (leadResponse && leadResponse.data) {
          fetchLeadData();
          setLead(leadResponse.data);
        }

        setShowNextActionModal(false);
        setNextActionData({ date: "", notes: "" });
      }
    } catch (error) {}
  };

  const formatFieldLabel = (field) => {
    const map = {
      status: "Status",
      next_interaction: "Next Interaction",
      next_interaction_notes: "Notes",
      next_interaction_user: "Assigned User",
      companyName: "Company",
      firstName: "First Name",
      city: "City",
    };

    return (
      map[field] ||
      field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const formatValue = (field, value) => {
    if (!value) return "—";
    if (field === "next_interaction") {
      return moment(value).format("DD-MM-YYYY hh:mm A");
    }

    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <span className="text-muted">—</span>;
    }

    return (
      <div className="small">
        {Object.entries(changes).map(([field, value], idx) => (
          <div key={idx} className="d-flex gap-1 align-items-center mb-1">
            <span className="fw-semibold text-nowrap">
              {formatFieldLabel(field)}:
            </span>

            <span className="text-muted text-truncate">
              {value.previous ?? "—"}
            </span>

            <span className="text-muted">→</span>

            <span className="text-success text-truncate">
              {String(value.new)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderLeadLogsTable = () => {
    if (!leadLogs?.length) {
      return (
        <div className="inventory-section">
          <h6 className="inventory-section-title">Lead Activity Logs</h6>
          <div className="inventory-table-wrapper">
            <p className="text-muted text-center mb-0">
              No lead activity available.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="inventory-section">
        <h6 className="inventory-section-title">Lead Activity Logs</h6>

        <div
          className="inventory-table-wrapper"
          style={{ maxHeight: "350px", overflowY: "auto" }}
        >
          <table className="inventory-table">
            <thead>
              <tr>
                <th style={{ width: "140px" }}>Updated By</th>
                <th style={{ width: "160px" }}>Date & Time</th>
                <th style={{ width: "90px" }}>Action</th>
                <th>Changes</th>
              </tr>
            </thead>

            <tbody>
              {leadLogs.map((log, idx) => (
                <tr key={idx}>
                  <td className="text-nowrap">{log.updated_by || "—"}</td>

                  <td className="small text-nowrap">
                    {moment(log.updated_on, "DD MMM YYYY, hh:mm A").format(
                      "DD-MM-YYYY hh:mm A",
                    )}
                  </td>

                  <td>
                    <Badge bg="secondary" pill>
                      {log.action}
                    </Badge>
                  </td>

                  <td>{renderChanges(log.changes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderInteractionsTable = () => {
    if (loading) {
      return (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }

    if (!leadData || leadData.length === 0) {
      return (
        <div className="inventory-section">
          <h6 className="inventory-section-title">Interactions</h6>
          <div className="inventory-table-wrapper">
            <p className="text-muted text-center mb-0">
              No interactions available.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="inventory-section">
        <h6 className="inventory-section-title">Interactions</h6>

        <div
          className="inventory-table-wrapper"
          style={{ maxHeight: "250px", overflowY: "auto" }}
        >
          <table className="inventory-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Date</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {leadData.map((interaction, idx) => (
                <tr key={idx}>
                  <td>
                    {interaction.created_by
                      ? `${interaction.created_by.first_name} ${interaction.created_by.last_name}`
                      : "—"}
                  </td>
                  <td>
                    {interaction.previous_interaction_date}{" "}
                    {interaction.previous_interaction_time}
                  </td>
                  <td>{interaction.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderNextActionTable = () => {
    if (!lead?.next_interaction) return null;

    return (
      <div className="inventory-section mb-3">
        <h6 className="inventory-section-title">Next Interaction</h6>

        <div className="inventory-table-wrapper">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Assigned To</th>
                <th>Date & Time</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {lead?.next_interaction_user
                    ? `${lead.next_interaction_user.first_name} ${lead.next_interaction_user.last_name}`
                    : "—"}
                </td>
                <td>
                  {moment(lead.next_interaction).format("DD-MM-YYYY hh:mm A")}
                </td>
                <td>{lead.next_interaction_notes || "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        dialogClassName="custom-modal-width"
      >
        <Modal.Header closeButton>
          <Modal.Title className="flex-grow-1">
            🧾 {lead?.firstName} {lead?.lastName || ""}
          </Modal.Title>

          <div className="ml-auto">
            <Button
              size="sm"
              variant="primary"
              className="mr-2"
              onClick={() => setShowNextActionModal(true)}
            >
              Next Action
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowInteractionModal(true)}
            >
              Last Interaction
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : error ? (
            <div
              className="text-center"
              style={{ color: "red", marginTop: "15px" }}
            >
              <p>{error}</p>
            </div>
          ) : (
            <Container>
              <Row className="mb-3">
                <Col>
                  <Card className="mb-3 shadow-sm">
                    <Card.Body className="small">
                      <Row>
                        {/* LEFT */}
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Created By:</strong>{" "}
                            {lead?.created_by?.first_name}{" "}
                            {lead?.created_by?.last_name}
                          </p>
                          <p className="mb-1">
                            <strong>Created At:</strong>{" "}
                            {moment(lead?.created_at).format("DD-MM-YYYY")}
                          </p>
                          <p className="mb-1">
                            <strong>Status:</strong> {lead?.status}
                          </p>

                          <p className="mb-1">
                            <strong>{labels.customerName}:</strong>{" "}
                            {lead?.firstName} {lead?.lastName}
                          </p>
                          <p className="mb-1">
                            <strong>{labels.companyName}:</strong>{" "}
                            {lead?.companyName || "—"}
                          </p>
                          <p className="mb-1">
                            <strong>Email:</strong> {lead?.email || "—"}
                          </p>
                        </Col>

                        {/* RIGHT */}
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Phone:</strong> {lead?.mobile || "—"}
                          </p>
                          <p className="mb-1">
                            <strong>Source:</strong> {lead?.source || "—"}
                          </p>
                          <p className="mb-1">
                            <strong>Address:</strong>{" "}
                            {`${lead?.address || ""} ${lead?.city || ""} ${lead?.state || ""}`}
                          </p>
                          <p className="mb-1">
                            <strong>Notes:</strong> {lead?.notes || "—"}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                {renderNextActionTable()}
                {renderInteractionsTable()}
              </Row>
              <Row>{renderLeadLogsTable()}</Row>
            </Container>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showNextActionModal}
        onHide={() => setShowNextActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Next Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-1">
              <Form.Label>Next Interaction Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={nextActionData.date}
                required
                onChange={(e) =>
                  setNextActionData({ ...nextActionData, date: e.target.value })
                }
                isInvalid={!!errors.date}
              />
              <Form.Control.Feedback type="invalid">
                {errors.date}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={nextActionData.notes}
                required
                onChange={(e) =>
                  setNextActionData({
                    ...nextActionData,
                    notes: e.target.value,
                  })
                }
                isInvalid={!!errors.notes}
              />
              <Form.Control.Feedback type="invalid">
                {errors.notes}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowNextActionModal(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handleNextActionSubmit}>
            Save Next Action
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInteractionModal}
        onHide={() => setShowInteractionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Interaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-1">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={interactionData.date}
                required
                onChange={(e) =>
                  setInteractionData({
                    ...interactionData,
                    date: e.target.value,
                  })
                }
                isInvalid={!!errors.date}
              />
              <Form.Control.Feedback type="invalid">
                {errors.date}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label>Time</Form.Label>
              <Form.Control
                type="time"
                value={interactionData.time}
                required
                onChange={(e) =>
                  setInteractionData({
                    ...interactionData,
                    time: e.target.value,
                  })
                }
                isInvalid={!!errors.time}
              />
              <Form.Control.Feedback type="invalid">
                {errors.time}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={interactionData.notes}
                required
                onChange={(e) =>
                  setInteractionData({
                    ...interactionData,
                    notes: e.target.value,
                  })
                }
                isInvalid={!!errors.notes}
              />
              <Form.Control.Feedback type="invalid">
                {errors.notes}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInteractionModal(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handleInteractionSubmit}>
            Save Interaction
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LeadViewModal;
